import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Users } from 'lucide-react';
import { ROUTES } from '../../app/config';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  Select,
  SkeletonList,
} from '../../components/ui';
import { useAdminApplications } from '../../services/applications.service';

/**
 * Quien entra a cada aplicacion. Solo de lectura.
 *
 * ## Por que aqui no se toca nada
 *
 * El acceso se concede persona a persona, y se hace en Personas: se entra a
 * alguien y se marcan sus aplicaciones. Esta pantalla es la misma informacion
 * leida al reves —de la aplicacion hacia las personas— porque «quien tiene
 * acceso a Portainer» es una pregunta que se hace tal cual y que, persona a
 * persona, obligaria a abrir a todo el mundo de uno en uno.
 *
 * Dejarla tambien editable seria el mismo permiso con dos mandos. Cuando dos
 * pantallas escriben lo mismo, tarde o temprano dicen cosas distintas, y la
 * respuesta a «quien le quito esto» deja de tener un solo sitio donde mirar.
 *
 * Antes se concedia ademas por rol de Keycloak. Se quito: obligaba a inventar
 * un rol por cada combinacion de aplicaciones, y una casilla marcada por rol no
 * se podia quitar a una sola persona sin un sistema de excepciones.
 */
export function AdminAccessPage(): JSX.Element {
  const { data: applications, isLoading, isError, refetch } = useAdminApplications();
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!selectedId && applications && applications.length > 0) {
      setSelectedId(applications[0].id);
    }
  }, [applications, selectedId]);

  const selected = applications?.find((application) => application.id === selectedId);

  if (isError) {
    return (
      <>
        <PageHeader title="Accesos" />
        <ErrorState onRetry={() => void refetch()} />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Accesos" />
        <SkeletonList rows={4} />
      </>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <>
        <PageHeader title="Accesos" description="Quién entra a cada aplicación." />
        <EmptyState
          icon={<KeyRound className="h-6 w-6" aria-hidden />}
          title="Todavía no hay aplicaciones registradas"
          description="Registra una aplicación antes de mirar sus accesos."
          action={
            <Link to={ROUTES.adminApps}>
              <Button>Ir a aplicaciones</Button>
            </Link>
          }
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Accesos"
        description="Quién entra a cada aplicación. Para cambiarlo, entra a la persona."
      />

      <div className="mb-6 max-w-md">
        <Select
          label="Aplicación"
          value={selectedId}
          onChange={(event) => setSelectedId(event.target.value)}
          options={applications.map((application) => ({
            value: application.id,
            label: application.name,
          }))}
        />
      </div>

      {selected ? (
        <Card>
          <CardHeader
            title={
              selected.accessUsers.length === 1
                ? '1 persona con acceso'
                : `${selected.accessUsers.length} personas con acceso`
            }
            description="El super administrador entra siempre, aparezca o no en esta lista."
            actions={
              <Link to={ROUTES.adminUsers}>
                <Button variant="secondary" leftIcon={<Users className="h-4 w-4" aria-hidden />}>
                  Gestionar en Personas
                </Button>
              </Link>
            }
          />
          <CardBody>
            {selected.accessUsers.length === 0 ? (
              <p className="text-sm text-text-muted">
                Nadie tiene concedida esta aplicación. Se concede desde Personas, entrando a cada
                una.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {selected.accessUsers.map((access) => (
                  <li key={access.id} className="py-2.5">
                    <p className="truncate text-sm text-text-primary">
                      {access.displayName ?? access.email ?? 'Persona'}
                    </p>
                    <p className="truncate font-mono text-[12px] text-text-muted">
                      {access.email ?? access.keycloakUserSub}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      ) : null}
    </>
  );
}
